import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {classify} from 'src/utils/classify';
import {
  ChatBubble,
  ChatContent,
  ChatBody,
  BodyMedium,
  CHAT_BUBBLE_ORIENTATION,
  ChatAnchor,
} from '@spaced-out/ui-design-system';
import type {
  ActionEvent,
  ChatRole,
  Part,
} from 'src/components/agentic-ui/types';
import {ThinkingWidget} from './widgets/thinking-widget';
import {WidgetRenderer} from './widgets';
import {MessageFeedback} from './message-feedback';
import css from './default-message-view.css';


export type FlattenedPart = Part & {
  messageId: string;
  messageRole: ChatRole;
  showAssistantAnchor: boolean;
  isLastPartInMessage: boolean;
  feedbackRating?: number;
  feedbackComment?: string;
};
export type DefaultMessagePartViewProps = {
  part: FlattenedPart;
  role: ChatRole;
  publishMessageAction?: (action: ActionEvent) => void;
  onWidgetResize?: () => void;
  /**
   * When true, renders the part within a chat bubble (for plain text parts).
   * Default: true
   */
  showChatBubble?: boolean;
  /**
   * Optional id used for the `data-message-part-id` attribute.
   * Defaults to `part.id` when present.
   */
  messagePartId?: string;
  showAssistantAnchor: boolean;
  onSubmitFeedback?: (
    messageId: string,
    rating: number,
    comment: string,
  ) => Promise<void>;
  showFeedback: boolean;
};

export function DefaultMessagePartView({
  part,
  role,
  publishMessageAction,
  onWidgetResize,
  showChatBubble = true,
  messagePartId,
  showAssistantAnchor,
  onSubmitFeedback,
  showFeedback,
}: DefaultMessagePartViewProps) {
  const key = messagePartId ?? part.id ?? '';

  const effectiveShowFeedback =
    showFeedback &&
    part.isLastPartInMessage &&
    role === 'assistant' &&
    onSubmitFeedback &&
    part.messageId;

  if (part.type === 'plain_text' && part.data !== '') {
    const content =
      role === 'user' ? (
        <ChatBody withBgColor>
          <BodyMedium>{part.data}</BodyMedium>
        </ChatBody>
      ) : (
        <ChatBody classNames={{wrapper: css.chatBodyWrapper}}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.data}</ReactMarkdown>
        </ChatBody>
      );

    return (
      <div data-message-part-id={key} className={css.messagePartItem}>
        {showChatBubble ? (
          <ChatBubble
            orientation={
              role === 'user'
                ? CHAT_BUBBLE_ORIENTATION.right
                : CHAT_BUBBLE_ORIENTATION.left
            }
          >
            {showAssistantAnchor && <ChatAnchor isAI={true} />}
            <ChatContent classNames={{wrapper: css.chatContentWrapper}}>
              {content}
            </ChatContent>
          </ChatBubble>
        ) : (
          content
        )}
        {effectiveShowFeedback && (
          <MessageFeedback
            messageId={part.messageId!}
            savedRating={part.feedbackRating}
            savedComment={part.feedbackComment}
            onSubmit={onSubmitFeedback}
          />
        )}
      </div>
    );
  }

  if (part.type === 'thinking') {
    return (
      <div
        data-message-part-id={key}
        className={classify(css.messagePartItem, css.widget)}
      >
        <ThinkingWidget text={part.data as string} />
      </div>
    );
  }

  if (part.type === 'widget') {
    return (
      <div
        data-message-part-id={key}
        className={classify(css.messagePartItem, css.widget)}
      >
        <WidgetRenderer
          part={part}
          publishMessageAction={publishMessageAction}
          onResize={onWidgetResize}
        />
      </div>
    );
  }

  // action / non-visual
  return null;
}
